import { useToast } from "hooks/useToasts";
import { useEffect } from "react";
import type { UseFormGetValues, UseFormSetValue, UseFormTrigger } from "react-hook-form";

import type { FormDataType } from "../../components/FormDataTypes";
import type { FormType, LPQuoteInput, QuoteLocation } from "../../types/Rater.types";
import { NEW_BUSINESS_REQUIRED_LOCATION_FORMS } from "../rules";
import { removeFormsWithToast } from "../utils/removeFormsWithToast";
import { useDeductibleRules } from "./useDeductibleRules";
import { useLiabilityRules } from "./useLiabilityRules";
import { useOccupancyRules } from "./useOccupancyRules";
import { usePerilRules } from "./usePerilRules";
import { useReplacementCostPercentageRules } from "./useReplacementCostPercentageRules";
import { useValuationRules } from "./useValuationRules";

export type BusinessRulesArgs = {
  forms: FormType[];
  tabItem: number;
  removeForm: (index: number) => void;
  appendForm: (form: FormType) => void;
  getValues: UseFormGetValues<LPQuoteInput>;
  setValue: UseFormSetValue<LPQuoteInput>;
  trigger: UseFormTrigger<LPQuoteInput>;
  formsData: Record<string, FormDataType>;
  locationValues: QuoteLocation;
  liabilityForm?: string;
  liability?: number;
  effectiveDate?: string;
  enabled: boolean;
  htmlIdGenerator: () => () => string;
};

function useRequiredNewBusinessLocationForms({ enabled, forms, appendForm, htmlIdGenerator }: BusinessRulesArgs) {
  useEffect(() => {
    if (!enabled) return;

    NEW_BUSINESS_REQUIRED_LOCATION_FORMS.forEach((formName) => {
      if (forms.some((form) => form.name === formName)) return;

      appendForm({
        formID: htmlIdGenerator()(),
        name: formName,
        fields: undefined,
      });
    });
  }, [appendForm, enabled, forms, htmlIdGenerator]);
}

function useRemoveInvalidForms({
  forms,
  getValues,
  tabItem,
  removeForm,
  locationValues,
  formsData,
  effectiveDate,
}: BusinessRulesArgs) {
  const { addToast } = useToast();
  useEffect(() => {
    if (!effectiveDate) return;

    const rateOptions = getValues();
    const formsToRemove = forms
      .map((form, index) => ({ form, index }))
      .filter(({ form }) => {
        const formData = formsData[form.name];

        if (!formData) return true;
        if (!formData?.addable) return false;

        return !formData.addable(rateOptions, tabItem);
      });
    removeFormsWithToast({
      formsToRemove,
      formsData,
      addToast,
      removeForm,
      reason: `because ${formsToRemove.length === 1 ? "it is" : "they are"} no longer applicable to the current quote`,
    });
  }, [locationValues, forms, formsData, effectiveDate, getValues, tabItem, addToast, removeForm]);
}

export function useBusinessRules(args: BusinessRulesArgs) {
  useRequiredNewBusinessLocationForms(args);
  const { valuationOptions, allowedValuationKeys } = usePerilRules(args);
  const { deductibleOptions, allowedDeductibleKeys } = useDeductibleRules(args);
  const { replacementCostPercentageOptions, allowedReplacementCostPercentages } =
    useReplacementCostPercentageRules(args);
  useValuationRules(args);
  useLiabilityRules(args);
  useOccupancyRules(args);
  useRemoveInvalidForms(args);

  return {
    valuationOptions,
    allowedValuationKeys,
    deductibleOptions,
    allowedDeductibleKeys,
    replacementCostPercentageOptions,
    allowedReplacementCostPercentages,
  };
}
